import { api } from "./api";
import type { ChatMessage, ChatSession } from "@/types";

export const WELCOME_MESSAGE_TEXT =
  "I'm here to help you understand your report. Ask anything; no judgment.";

interface BackendCitationLink {
  cite: string;
  url: string;
}

interface BackendMessage {
  id: string;
  session_id: string;
  sender: "user" | "assistant";
  text: string;
  highlights: string[];
  citation_links: BackendCitationLink[];
  created_at: string;
}

interface SendMessageResponse {
  user_message: BackendMessage;
  assistant_message: BackendMessage;
}

function toFrontendMessage(m: BackendMessage): ChatMessage {
  return {
    id: m.id,
    sender: m.sender,
    text: m.text,
    timestamp: m.created_at,
    highlights: m.highlights?.length ? m.highlights : undefined,
    citationLinks: m.citation_links?.length ? m.citation_links : undefined,
  };
}

/** UI-only welcome — not stored in the database. */
export function withWelcomeMessage(
  testId: string,
  messages: ChatMessage[],
): ChatMessage[] {
  if (messages.length > 0) return messages;
  return [
    {
      id: "welcome",
      testId,
      sender: "assistant",
      text: WELCOME_MESSAGE_TEXT,
      timestamp: new Date().toISOString(),
      highlights: [],
    },
  ];
}

// ── Active API ──────────────────────────────────────────────────

export async function listSessions(token?: string): Promise<ChatSession[]> {
  if (!token) throw new Error("A report token is required to list sessions");
  return api<ChatSession[]>(`/api/chat/sessions?token=${encodeURIComponent(token)}`);
}

export async function createSession(token?: string): Promise<ChatSession> {
  if (!token) throw new Error("A report token is required to create a session");
  return api<ChatSession>(`/api/chat/sessions`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export async function getSessionMessages(
  sessionId: string,
  token?: string,
): Promise<ChatMessage[]> {
  if (!token) throw new Error("A report token is required to load messages");
  const msgs = await api<BackendMessage[]>(
    `/api/chat/sessions/${sessionId}/messages?token=${encodeURIComponent(token)}`,
  );
  return msgs.map(toFrontendMessage);
}

export async function sendMessage(
  sessionId: string,
  text: string,
  token?: string,
): Promise<ChatMessage[]> {
  if (!token) throw new Error("A report token is required to send messages");
  const res = await api<SendMessageResponse>(
    `/api/chat/sessions/${sessionId}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ text, token }),
    },
  );
  return [
    toFrontendMessage(res.user_message),
    toFrontendMessage(res.assistant_message),
  ];
}
